/**
 * «Ціни РДН»: tomorrow's hourly prices pasted from the market operator's
 * site, ₴/MWh, one value per line. Checked as typed: each value a number not
 * below 0, as many values as the day has hours (24; 23 or 25 on a
 * clock-change day). The server checks the same before saving. Pure.
 */
import type { Locale, MessageKey } from '../../i18n/messages.js';
import { countLabel, fill } from './admin-format.js';

export type DamPasteError =
  | { kind: 'not_number'; line: number; raw: string }
  | { kind: 'ambiguous'; line: number; raw: string }
  | { kind: 'negative'; line: number }
  | { kind: 'count'; got: number; expected: number };

/** `null` — nothing pasted yet. */
export type DamPasteResult = { values: number[] } | { error: DamPasteError } | null;

const SPACES = /[\s\u00a0\u202f]/g;
/** «4210», «4210,55», «4210.55». */
const PLAIN = /^-?\d+([.,]\d+)?$/;
/** Thousands by spaces, a comma or a dot for decimals: «4 210», «4 210,55», «4 210.55». */
const SPACED = /^-?\d{1,3}([ \u00a0\u202f]\d{3})+([.,]\d+)?$/;
/** Thousands by a comma, decimals by a dot (and the other way round): «4,210.55», «4.210,55». */
const GROUPED_COMMA = /^-?\d{1,3}(,\d{3})+\.\d+$/;
const GROUPED_DOT = /^-?\d{1,3}(\.\d{3})+,\d+$/;
/** «5,600» / «5.600»: thousands or decimals — not guessed. */
const AMBIGUOUS = /^-?\d{1,3}[.,]\d{3}$/;

/**
 * One cell → number. Grouping spaces only in groups of three, so «1 4210,55»
 * (an hour and a price) is not read as 14 210,55.
 */
function parseValue(raw: string): number | 'not_number' | 'ambiguous' {
  const s = raw.replace(/\u2212/g, '-');
  if (AMBIGUOUS.test(s)) return 'ambiguous';
  if (PLAIN.test(s)) return Number(s.replace(',', '.'));
  if (SPACED.test(s)) return Number(s.replace(SPACES, '').replace(',', '.'));
  if (GROUPED_COMMA.test(s)) return Number(s.replace(/,/g, ''));
  if (GROUPED_DOT.test(s)) return Number(s.replace(/\./g, '').replace(',', '.'));
  return 'not_number';
}

/**
 * One value per line; a tab or «;» also separates values (cells copied from
 * a table). Empty lines are skipped; line numbers are those of the text.
 */
export function parseDamPaste(text: string, expected: number): DamPasteResult {
  const values: number[] = [];
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    for (const cell of (lines[i] ?? '').split(/[\t;]/)) {
      const raw = cell.trim();
      if (raw === '') continue;
      const value = parseValue(raw);
      if (typeof value === 'string') return { error: { kind: value, line: i + 1, raw } };
      if (value < 0) return { error: { kind: 'negative', line: i + 1 } };
      values.push(value);
    }
  }
  if (values.length === 0) return null;
  if (values.length !== expected) return { error: { kind: 'count', got: values.length, expected } };
  return { values };
}

/** «… Для 21.10.2026 потрібно 24 значення.»; on a clock-change day also which one (23 or 25 hours). */
export function damPricesIntro(
  hours: number,
  t: (k: MessageKey) => string,
  locale: Locale,
  date: string,
): string {
  const intro = fill(t('operator.prices.intro'), {
    date,
    values: countLabel(t, locale, 'operator.count.values', hours),
  });
  if (hours === 23) return intro + t('operator.prices.intro.short_day');
  if (hours === 25) return intro + t('operator.prices.intro.long_day');
  return intro;
}

/** «Рядок 3: «abc» — не число.», «Знайдено 23 значення, для 21.10.2026 потрібно 24. Ціни не збережено.» */
export function damPasteMessage(
  error: DamPasteError,
  t: (k: MessageKey) => string,
  locale: Locale,
  date: string,
): string {
  switch (error.kind) {
    case 'not_number':
      return fill(t('operator.prices.err.not_number'), { line: error.line, raw: error.raw });
    case 'ambiguous':
      return fill(t('operator.prices.err.ambiguous'), { line: error.line, raw: error.raw });
    case 'negative':
      return fill(t('operator.prices.err.negative'), { line: error.line });
    case 'count':
      return fill(t('operator.prices.err.count'), {
        values: countLabel(t, locale, 'operator.count.values', error.got),
        date,
        hours: error.expected,
      });
  }
}
